<template>
  <v-container fluid fill-height>
    <v-row align="center" justify="center">
      <v-col cols="12">
        <v-card flat class="mx-auto public-form transparent">
          <v-card outlined id="forgot-password">
            <v-card-text class="text-center">
              <v-img :src="$app.opts.authLogoSrc" :alt="$app.opts.title" max-height="250" contain />
            </v-card-text>

            <div v-if="!sent">
              <v-card-text class="text-center title">
                <h3>{{ $t('forgotPassword') }}</h3>
              </v-card-text>
              <FormErrors :show="showResourceErrors" :errors="resourceErrors" />
              <v-card-text class="py-2">
                <v-form>
                  <v-text-field
                    append-icon="$vuetify.icons.values.user"
                    v-model.trim="form.login"
                    :error-messages="loginErrors"
                    v-bind:label="$t('login')"
                    required
                    @input="$v.form.login.$touch()"
                    @blur="$v.form.login.$touch()"
                  ></v-text-field>
                </v-form>
              </v-card-text>
              <v-card-text class="text-center title">
                <h5>{{ $t('resetLink') }}</h5>
              </v-card-text>
              <v-card-actions>
                <v-row>
                  <v-col cols="6">
                    <v-btn block color="accent" @click="sendResetLink('email')" depressed>{{ $t('byEmail') }}</v-btn>
                  </v-col>
                  <v-col cols="6">
                    <v-btn block color="accent" @click="sendResetLink('sms')" depressed>{{ $t('bySMS') }}</v-btn>
                  </v-col>
                </v-row>
              </v-card-actions>
            </div>

            <div v-if="sent">
              <v-card-text class="py-2 text-center title">
                <h3 v-if="service == 'email'">{{ $t('emailResetLinkSent') }}</h3>
                <h3 v-if="service == 'sms'">{{ $t('smsResetLinkSent') }}</h3>
              </v-card-text>
              <v-card-text class="py-2 text-center subheading">
                <p v-if="service == 'email'">{{ $t('emailResetLinkSentTxt') }}</p>
                <p v-if="service == 'sms'">{{ $t('smsResetLinkSentTxt') }}</p>
              </v-card-text>
              <v-card-text class="py-2 text-center" v-if="!verified">{{ $t('verificationWarning') }}</v-card-text>
            </div>

            <v-card-text class="py-2 text-center">
              {{ $t('labels.backTo') }}
              <br />
              <v-btn text color="default" :to="{ name: 'signin' }">
                {{ $t('signin') }}
              </v-btn>
            </v-card-text>
          </v-card>

          <v-card flat class="my-3 transparent text-center">
            <LanguageField use-buttons v-model="myLocale" immediate fieldClass="elevation-0" />
          </v-card>
        </v-card>
      </v-col>
    </v-row>
    <RefreshApp />
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { required } from 'vuelidate/lib/validators'
import { BaseForm, FormErrors, LanguageField, RefreshApp } from '@argon/app/components'
import { NAMESPACE } from '../store'
import { NAMESPACE as ME_NAMESPACE } from '@argon/iam/me/store'

export default {
  extends: BaseForm,
  components: { FormErrors, LanguageField, RefreshApp },
  validations: {
    form: {
      login: { required }
    }
  },
  data: () => ({
    form: {
      login: ''
    },
    service: '',
    sent: false,
    verified: false
  }),
  computed: {
    ...mapGetters(ME_NAMESPACE, ['locale']),
    myLocale: {
      get() {
        return this.locale
      },
      set(v) {
        this.setLocale(v)
      }
    },
    loginErrors() {
      const errors = []
      if (!this.$v.form.login.$dirty) return errors
      !this.$v.form.login.required && errors.push(this.$t('required'))
      this.getServerErrors('login', errors)
      return errors
    }
  },
  methods: {
    ...mapActions(NAMESPACE, ['requestReset']),
    ...mapActions(ME_NAMESPACE, ['setLocale']),
    sendResetLink(service) {
      if (this.$v.form.$invalid) {
        return this.$v.form.$touch()
      }
      const payload = {
        login: this.form.login,
        transport: service || 'email'
      }
      this.requestReset(payload)
        .then((resp) => {
          this.service = service
          this.sent = true
          this.verified = resp.email_verified || false
        })
        .catch(this.setServerErrors)
    }
  }
}
</script>

<i18n>
{
  "en": {
    "backTo": "@:labels.backTo",
    "byEmail": "Email",
    "bySMS": "SMS",
    "emailResetLinkSent": "Check Your Inbox",
    "emailResetLinkSentTxt": "If such a user exists, an email with the required instructions will be sent shortly.",
    "forgotPassword": "Forgot Your Password ?",
    "login": "@:labels.login",
    "required": "@:labels.required",
    "resetLink": "Send Reset Link",
    "signin": "@:labels.signin",
    "smsResetLinkSent": "Check Your SMS",
    "smsResetLinkSentTxt": "If such a user exists, an SMS with the required instructions will be sent shortly.",
    "verificationWarning": "If your acount is not yet verified, you will need to verify it before being able to change your password."
  },
  "fr": {
    "backTo": "@:labels.backTo",
    "byEmail": "Courriel",
    "bySMS": "Texto",
    "emailResetLinkSent": "Vérifiez votre courriel",
    "emailResetLinkSentTxt": "Si cet utilisateur existe, un courriel avec les instructions nécéssaires sera envoyé incessament.",
    "forgotPassword": "Perdu votre mot de passe ?",
    "login": "@:labels.signin",
    "required": "@:labels.required",
    "resetLink": "Envoyer un lien de réinitialisation",
    "signin": "@:labels.signin",
    "smsResetLinkSent": "Vérifiez vos textos",
    "smsResetLinkSentTxt": "Si cet utilisateur existe, un texto avec les instructions nécéssaires sera envoyé incessament.",
    "verificationWarning": "Si votre compte n'est pas encore vérifié, vous devez le vérifier avant de pouvoir changer de mot de passe."
  }
}
</i18n>
